import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * PersonaContext — HARD-ISOLATED state for the Persona studio.
 *
 * This is Persona's OWN React context. It does NOT live in the shared
 * core/AppContext, so adding/removing persona state can never affect the other
 * menus. It reads the auth token straight from localStorage (same place the
 * shared app stores it) so it stays self-contained; RBAC checks are passed in
 * from the view via the shared `can()` (authorization is global by design).
 */
const API_BASE = '/api';
const PersonaContext = createContext(null);
export const usePersona = () => useContext(PersonaContext);

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
  };
}

export function PersonaProvider({ children }) {
  const [personas, setPersonas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  // chat state (per selected persona)
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [audio, setAudio] = useState(null);
  // Reply mode — GLOBAL across all personas, persisted in localStorage:
  //   'ai_voice'       — preset Polly/edge voice, synthesized inline (fast).
  //   'cloned_voice'   — GPU clones the persona's voice (audio only); reply is
  //                      held until the clone job is DONE, then revealed + played.
  //   'cloned_lipsync' — GPU clones voice + SadTalker lip-sync (video); reply is
  //                      held until the video job is DONE, then revealed + played.
  const [mode, setModeState] = useState(() => {
    const saved = (typeof localStorage !== 'undefined' && localStorage.getItem('persona_mode')) || '';
    return ['ai_voice', 'cloned_voice', 'cloned_lipsync', 'echomimic_lipsync'].includes(saved) ? saved : 'ai_voice';
  });
  const setMode = useCallback((m) => {
    setModeState(m);
    try { localStorage.setItem('persona_mode', m); } catch (e) { /* ignore */ }
  }, []);
  const [renderJob, setRenderJob] = useState(null);

  // "Hide text until media ready" flow: the assistant reply is held here
  // (text + audio + render handle) and NOT shown in the transcript until the
  // selected media is ready — voice cloned (audio) or lip-sync done (video).
  // The view drives reveal() when its poll sees the job reach a terminal state.
  const [pendingReply, setPendingReply] = useState(null); // {content, audio, render} | null
  const [renderLogs, setRenderLogs] = useState([]);        // per-persona job history

  // model discovery
  const [models, setModels] = useState({ selected: '', available: [] });

  const refreshPersonas = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/personas`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPersonas(data.personas || []);
      } else if (res.status === 403) {
        setStatus('You lack the use_persona privilege.');
      }
    } catch (e) { setStatus('Error loading personas.'); }
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/persona-models`, { headers: authHeaders() });
      if (res.ok) setModels(await res.json());
    } catch (e) { /* non-fatal */ }
  }, []);

  const createPersona = useCallback(async (fields) => {
    setStatus('Creating persona...');
    try {
      const res = await fetch(`${API_BASE}/personas`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(fields),
      });
      const data = await res.json();
      if (res.ok && data.status === 'SUCCESS') {
        setStatus(`Persona '${data.persona.name}' created.`);
        refreshPersonas();
        return data.persona;
      }
      setStatus(`Create failed: ${data.error || data.status}`);
    } catch (e) { setStatus('Error creating persona.'); }
    return null;
  }, [refreshPersonas]);

  const updatePersona = useCallback(async (id, fields) => {
    setStatus('Saving...');
    try {
      const res = await fetch(`${API_BASE}/personas/${id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(fields),
      });
      const data = await res.json();
      setStatus(res.ok && data.status === 'SUCCESS' ? 'Saved.' : `Save failed: ${data.error || data.status}`);
      refreshPersonas();
    } catch (e) { setStatus('Error saving persona.'); }
  }, [refreshPersonas]);

  const deletePersona = useCallback(async (id) => {
    setStatus('Deleting persona...');
    try {
      const res = await fetch(`${API_BASE}/personas/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.status === 'SUCCESS') {
        setStatus('Persona deleted.');
        if (selectedId === id) { setSelectedId(null); setHistory([]); }
        refreshPersonas();
      } else {
        setStatus(`Delete failed: ${data.error || data.status}`);
      }
    } catch (e) { setStatus('Error deleting persona.'); }
  }, [refreshPersonas, selectedId]);

  const uploadAsset = useCallback(async (id, kind, file) => {
    if (!file) return;
    setStatus(`Uploading ${kind}...`);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await fetch(`${API_BASE}/personas/${id}/asset`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ kind, b64, filename: file.name || kind }),
      });
      const data = await res.json();
      setStatus(res.ok && data.status === 'SUCCESS' ? `${kind} uploaded.` : `Upload failed: ${data.error || data.status}`);
      refreshPersonas();
    } catch (e) { setStatus(`Error uploading ${kind}.`); }
  }, [refreshPersonas]);

  const deleteAsset = useCallback(async (id, kind) => {
    setStatus(`Removing ${kind}...`);
    try {
      const res = await fetch(`${API_BASE}/personas/${id}/asset`, {
        method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ kind }),
      });
      const data = await res.json();
      setStatus(res.ok && data.status === 'SUCCESS' ? `${kind} removed.` : `Remove failed: ${data.error || data.status}`);
      refreshPersonas();
    } catch (e) { setStatus(`Error removing ${kind}.`); }
  }, [refreshPersonas]);

  const selectPersona = useCallback((id) => {
    setSelectedId(id);
    setHistory([]);
    setAudio(null);
    setRenderJob(null);
    setPendingReply(null);
    setStatus('');
  }, []);

  // Coerce any model/content value to a safe display string. Some Bedrock model
  // body shapes return content as an array/object; rendering that directly as a
  // React child throws "Objects are not valid as a React child" and blanks the
  // whole page. Everything that lands in history MUST pass through this.
  const asText = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) return v.map(asText).join('');
    if (typeof v === 'object') {
      if (typeof v.text === 'string') return v.text;
      if (typeof v.content === 'string') return v.content;
      try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
  };

  // Reveal the held assistant reply into the transcript. Called by the view
  // when the required media is ready: immediately for voice-only (audio returns
  // with the chat response), or after the render job reaches DONE for video.
  // `videoUrl` (optional) is attached to the revealed message when present.
  // Reveal the held reply when a cloned job finishes. `job` is the DONE job
  // record: result_kind 'audio' -> play its mp3; 'video' -> embed its mp4.
  const revealPending = useCallback((job) => {
    const url = job && job.result_url ? job.result_url : null;
    const kind = job && job.result_kind ? job.result_kind : (job && job.kind) || null;
    setPendingReply(prev => {
      if (!prev) return null;
      const isVideo = kind === 'video';
      setHistory(h => [...h, {
        role: 'assistant',
        content: prev.content,
        video: isVideo ? url : null,
      }]);
      // Cloned-voice: play the returned mp3. Fallback to any inline audio held.
      if (!isVideo && url) setAudio(url);
      else if (prev.audio) setAudio(prev.audio);
      return null;
    });
  }, []);

  const sendMessage = useCallback(async () => {
    const msg = (input || '').trim();
    if (!msg || !selectedId) return;
    setBusy(true);
    setAudio(null);
    setRenderJob(null);
    setPendingReply(null);
    // Show the user's message immediately. The ASSISTANT reply is held hidden
    // (pendingReply) until its selected media is ready — EXCEPT in 'text' mode,
    // where it shows right away.
    const optimistic = [...history, { role: 'user', content: msg }];
    setHistory(optimistic);
    setInput('');
    setStatus(
      mode === 'cloned_lipsync' ? 'Thinking… response hidden until the lip-sync video is ready.'
      : mode === 'cloned_voice' ? 'Thinking… response hidden until the cloned voice is ready.'
      : 'Thinking…'
    );
    try {
      const res = await fetch(`${API_BASE}/personas/${selectedId}/chat`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ message: msg, history, mode }),
      });
      const data = await res.json();
      if (res.ok && (data.status === 'SUCCESS' || data.text)) {
        const replyText = asText(data.text);
        const replyAudio = data.audio_b64 ? `data:audio/mp3;base64,${data.audio_b64}` : null;
        // Modes that enqueue a GPU render job and hold the reply until DONE:
        // the two cloned modes AND the experimental EchoMimic mode.
        const isRenderMode = (mode === 'cloned_voice' || mode === 'cloned_lipsync' || mode === 'echomimic_lipsync');
        if (isRenderMode && data.render && data.render.job_id) {
          // CLONED modes: a GPU job was queued. Hold the reply; the view polls
          // the job and calls revealPending(url) when it reaches DONE. For
          // cloned_voice the url is an mp3 (played as audio); for cloned_lipsync
          // it's an mp4 (shown as video). result_kind on the job disambiguates.
          setPendingReply({ content: replyText, audio: null, render: data.render });
          setRenderJob(data.render);
          setStatus((mode === 'cloned_lipsync' || mode === 'echomimic_lipsync')
            ? 'Cloning voice & animating… response hidden until the video is ready.'
            : 'Cloning voice… response hidden until the cloned voice is ready.');
        } else if (isRenderMode && data.render && data.render.status && data.render.status !== 'QUEUED') {
          // Render could not be queued (e.g. MISSING_ASSETS/NO_QUEUE): reveal
          // the text now and surface why in the status.
          setHistory(h => [...h, { role: 'assistant', content: replyText, video: null }]);
          setPendingReply(null);
          setStatus(`Reply shown (render unavailable: ${data.render.message || data.render.status}).`);
        } else if (mode === 'ai_voice') {
          // AI VOICE: preset voice returns inline. Hold briefly so the reply
          // doesn't flash before the audio, then reveal text + audio together.
          setPendingReply({ content: replyText, audio: replyAudio, render: null });
          setStatus('Preparing voice… response appears when the voice is ready.');
          setTimeout(() => {
            setHistory(h => [...h, { role: 'assistant', content: replyText, video: null }]);
            if (replyAudio) setAudio(replyAudio);
            setPendingReply(null);
            setStatus(replyAudio ? 'Voice ready.' : 'Voice unavailable — showing text.');
          }, 500);
        } else {
          // Fallback (text / no media): show immediately.
          setHistory(h => [...h, { role: 'assistant', content: replyText, video: null }]);
          setPendingReply(null);
          setStatus('Response ready.');
        }
      } else {
        setHistory([...optimistic, { role: 'assistant', content: `Error: ${asText(data.error) || 'server error'}` }]);
        setStatus(`Error: ${data.error || 'server error'}`);
      }
    } catch (e) {
      setHistory([...optimistic, { role: 'assistant', content: 'Error: API connection error.' }]);
      setStatus('API connection error.');
    } finally {
      setBusy(false);
    }
  }, [input, selectedId, history, mode]);

  const pollJob = useCallback(async (jobId) => {
    try {
      const res = await fetch(`${API_BASE}/persona-jobs/${jobId}`, { headers: authHeaders() });
      if (res.ok) return await res.json();
    } catch (e) { /* ignore */ }
    return null;
  }, []);

  // Fetch recent render jobs for a persona (for the Render Logs tab).
  const fetchRenderLogs = useCallback(async (personaId) => {
    if (!personaId) return;
    try {
      const res = await fetch(`${API_BASE}/persona-jobs?persona_id=${encodeURIComponent(personaId)}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRenderLogs(Array.isArray(data.jobs) ? data.jobs : []);
      }
    } catch (e) { /* non-fatal */ }
  }, []);

  const value = {
    personas, selectedId, status, busy, history, input, audio, renderJob, models,
    pendingReply, renderLogs, mode,
    setInput, setMode, setStatus,
    refreshPersonas, fetchModels, createPersona, updatePersona, deletePersona,
    uploadAsset, deleteAsset, selectPersona, sendMessage, pollJob,
    revealPending, fetchRenderLogs,
  };
  return <PersonaContext.Provider value={value}>{children}</PersonaContext.Provider>;
}
